import { useMutation, useQueryClient } from '@tanstack/react-query'

import type {
  ApiError,
  ExtensionInstance,
  PaginatedApiResult,
} from '@mangovoice/types'

import { useExtensionsApi } from '../api/context'
import { EXTENSION_QUERY_KEYS } from '../api/endpoints'
import type {
  CreateExtensionPayload,
  CreateSharedVoicemailPayload,
  MassEditLinesPayload,
  UpdateExtensionPayload,
} from '../types/api.types'

const extractErrorMessage = (data: unknown, fallback: string): string => {
  if (data == null) return fallback
  if (typeof data === 'string') return data || fallback
  if (typeof data !== 'object') return fallback

  const record = data as Record<string, unknown>
  const parameters = record.parameters
  if (Array.isArray(parameters) && parameters.length > 0) {
    const details = parameters
      .map((parameter) => {
        if (parameter == null || typeof parameter !== 'object') return null
        const p = parameter as Record<string, unknown>
        const name = typeof p.name === 'string' ? p.name : 'field'
        const message =
          typeof p.message === 'string' ? p.message : 'Validation error'
        return `${name}: ${message}`
      })
      .filter((value): value is string => Boolean(value))
    if (details.length > 0) return details.join('; ')
  }

  if (typeof record.message === 'string' && record.message.trim() !== '') {
    return record.message
  }
  if (typeof record.detail === 'string' && record.detail.trim() !== '') {
    return record.detail
  }

  for (const [field, value] of Object.entries(record)) {
    if (field === 'type' || field === 'code' || value == null) continue
    if (typeof value === 'string' && value.trim() !== '') {
      return `${field}: ${value}`
    }
    if (Array.isArray(value) && value.length > 0) {
      return `${field}: ${value.map((item) => String(item)).join(', ')}`
    }
  }

  try {
    return JSON.stringify(data)
  } catch {
    return fallback
  }
}

const removeExtensionFromCachedLists = (
  queryClient: ReturnType<typeof useQueryClient>,
  pbxId: number | undefined | null,
  extensionId: number | string,
) => {
  queryClient.setQueriesData<PaginatedApiResult<ExtensionInstance>>(
    { queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId] },
    (previous) => {
      if (!previous?.results) return previous
      const nextResults = previous.results.filter(
        (extension) => String(extension.id) !== String(extensionId),
      )
      if (nextResults.length === previous.results.length) return previous
      return {
        ...previous,
        count: Math.max(0, (previous.count ?? 0) - 1),
        results: nextResults,
      }
    },
  )
}

/**
 * Mutation to delete an extension.
 * Invalidates extensions list so the table refreshes after delete.
 */
export const useDeleteExtension = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (extensionId: number | string) => {
      const { ok } = await api.deleteExtension(extensionId)
      if (!ok) {
        return Promise.reject(new Error('Failed to delete extension'))
      }
    },
    onSuccess: (_, extensionId) => {
      removeExtensionFromCachedLists(queryClient, pbxId, extensionId)
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Delete a line extension with optional reassign.
 */
export const useDeleteAndReassignLine = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      extensionId,
      reassignToExtensionId,
    }: {
      extensionId: number | string
      reassignToExtensionId: number | null
    }) => {
      if (pbxId == null) {
        return Promise.reject(
          new Error('PBX ID required for delete and reassign'),
        )
      }
      const { ok } = await api.deleteAndReassignLine(
        pbxId,
        extensionId,
        reassignToExtensionId,
      )
      if (!ok) {
        return Promise.reject(new Error('Failed to delete and reassign line'))
      }
    },
    onSuccess: (_, variables) => {
      removeExtensionFromCachedLists(queryClient, pbxId, variables.extensionId)
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to update an extension (line edit core fields and other type-specific edits).
 */
export const useUpdateExtension = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      extensionId,
      payload,
    }: {
      extensionId: number | string
      payload: UpdateExtensionPayload
    }) => {
      const { ok, data } = await api.updateExtension(extensionId, payload)
      if (!ok) {
        return Promise.reject(
          new Error(extractErrorMessage(data, 'Failed to update extension')),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: EXTENSION_QUERY_KEYS.detail(variables.extensionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Baseline extension create mutation for non-switchboard types.
 */
export const useCreateExtension = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateExtensionPayload) => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.createExtension(pbxId, payload)
      if (!ok) {
        return Promise.reject(
          new Error(extractErrorMessage(data, 'Failed to create extension')),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to create shared voicemail extension.
 */
export const useCreateSharedVoicemail = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateSharedVoicemailPayload) => {
      if (pbxId == null || Number.isNaN(pbxId)) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.createSharedVoicemail(pbxId, payload)
      if (!ok) {
        return Promise.reject(
          new Error(
            extractErrorMessage(data, 'Failed to create shared voicemail'),
          ),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to update extension user groups (virtual fax, shared voicemail).
 */
export const useUpdateExtensionUserGroups = (
  pbxId: number | undefined | null,
) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      extensionId,
      user_group_ids,
    }: {
      extensionId: number | string
      user_group_ids: number[]
    }) => {
      const { ok, data } = await api.updateExtensionUserGroups(extensionId, {
        user_group_ids,
      })
      if (!ok) {
        return Promise.reject(
          (data as ApiError) ?? new Error('Failed to update user groups'),
        )
      }
      return data as ExtensionInstance
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: EXTENSION_QUERY_KEYS.detail(variables.extensionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to resend secure fax link (virtual fax).
 */
export const useResendSecureFaxLink = (
  extensionId: number | string | undefined | null,
  pbxId: number | undefined | null,
) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (extensionId == null) {
        return Promise.reject(new Error('Extension ID is required'))
      }
      const { ok } = await api.resendSecureFaxLink(extensionId)
      if (!ok) {
        return Promise.reject(new Error('Failed to resend secure fax link'))
      }
    },
    onSuccess: () => {
      if (extensionId != null) {
        queryClient.invalidateQueries({
          queryKey: EXTENSION_QUERY_KEYS.detail(extensionId),
        })
      }
      if (pbxId != null) {
        queryClient.invalidateQueries({
          queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
        })
      }
    },
  })
}

/**
 * Mutation to bulk update location for selected line extensions.
 */
export const useUpdateLinesLocation = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      extension_ids: number[]
      location_id: number
    }) => {
      if (pbxId == null) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.updateLinesLocation({
        pbx_id: pbxId,
        extension_ids: params.extension_ids,
        location_id: params.location_id,
      })
      if (!ok || data == null) {
        return Promise.reject(new Error('Failed to update location'))
      }
      return data as { updated: number }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to mass edit selected line extensions.
 */
export const useMassEditLines = (pbxId: number | undefined | null) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: MassEditLinesPayload) => {
      if (pbxId == null) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.massEditLines({
        pbx_id: pbxId,
        extension_ids: params.extension_ids,
        ...(params.outbound_external_callerid !== undefined && {
          outbound_external_callerid: params.outbound_external_callerid,
        }),
        ...(params.use_local_presence !== undefined && {
          use_local_presence: params.use_local_presence,
        }),
        ...(params.preferred_codec !== undefined && {
          preferred_codec: params.preferred_codec,
        }),
        ...(params.location_id !== undefined && {
          location_id: params.location_id,
        }),
        ...(params.pbx_location_id !== undefined && {
          pbx_location_id: params.pbx_location_id,
        }),
      })
      if (!ok || data == null) {
        return Promise.reject(new Error('Failed to update lines'))
      }
      return data as { updated: number }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
      })
    },
  })
}

/**
 * Mutation to reset SIP secret for a line (internal-only support action).
 */
export const useResetSipSecret = (
  extensionId: number | string | undefined | null,
  pbxId: number | undefined | null,
) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (extensionId == null) {
        return Promise.reject(new Error('Extension ID is required'))
      }
      // Callers coerce a missing/bad route param to 0 or NaN, so a null check
      // would let /pbxes/0/... and /pbxes/NaN/... through and 404 silently.
      if (typeof pbxId !== 'number' || !Number.isFinite(pbxId) || pbxId <= 0) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.resetSipSecret(pbxId, extensionId)
      if (!ok) {
        // Surface the API's message (e.g. permission_denied) — this action is
        // superuser-only, so a staff user gets a 403 with a real explanation.
        return Promise.reject(
          new Error(extractErrorMessage(data, 'Failed to reset SIP secret')),
        )
      }
    },
    onSuccess: () => {
      if (extensionId != null) {
        queryClient.invalidateQueries({
          queryKey: EXTENSION_QUERY_KEYS.detail(extensionId),
        })
      }
      if (pbxId != null) {
        queryClient.invalidateQueries({
          queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
        })
      }
    },
  })
}

/**
 * Mutation to clear dial-now for a line (internal-only support action).
 */
export const useClearDialNow = (
  extensionId: number | string | undefined | null,
  pbxId: number | undefined | null,
) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (extensionId == null) {
        return Promise.reject(new Error('Extension ID is required'))
      }
      const { ok } = await api.clearDialNow(extensionId)
      if (!ok) {
        return Promise.reject(new Error('Failed to clear dial-now'))
      }
    },
    onSuccess: () => {
      if (extensionId != null) {
        queryClient.invalidateQueries({
          queryKey: EXTENSION_QUERY_KEYS.detail(extensionId),
        })
      }
      if (pbxId != null) {
        queryClient.invalidateQueries({
          queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
        })
      }
    },
  })
}

/**
 * Mutation to set line active state (internal-only support action).
 */
export const useSetLineActiveState = (
  extensionId: number | string | undefined | null,
  pbxId: number | undefined | null,
) => {
  const api = useExtensionsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (active: boolean) => {
      if (extensionId == null) {
        return Promise.reject(new Error('Extension ID is required'))
      }
      if (pbxId == null) {
        return Promise.reject(new Error('PBX ID is required'))
      }
      const { ok, data } = await api.setLineActiveState(
        pbxId,
        extensionId,
        active,
      )
      if (!ok) {
        return Promise.reject(new Error('Failed to set active state'))
      }
      return data as ExtensionInstance
    },
    onSuccess: () => {
      if (extensionId != null) {
        queryClient.invalidateQueries({
          queryKey: EXTENSION_QUERY_KEYS.detail(extensionId),
        })
      }
      if (pbxId != null) {
        queryClient.invalidateQueries({
          queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxId],
        })
      }
    },
  })
}
